import { useAuth } from '@/app/hooks/useAuth'
import { useCoupons } from '@/app/hooks/useCoupons'
import { GenerateRafflePdf } from '@/app/utils/generateRafflePdf'
import { Button } from '@/view/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/view/components/ui/dialog'
import { Separator } from '@/view/components/ui/separator'
import { useToast } from '@/view/components/ui/use-toast'
import { Stickynote } from 'iconsax-react'
import React, { useState } from 'react'

interface CouponModalProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  raffleId: string
  raffleTitle: string
  raffleDescription: string
  raffleBanner: string
}

const couponImage = import.meta.env.VITE_COUPON

const CouponModal: React.FC<CouponModalProps> = ({
  isOpen,
  onOpenChange,
  raffleId,
}) => {
  const { user } = useAuth()
  const { toast } = useToast()
  const [isExporting, setIsExporting] = useState(false)
  const { data: couponsResponse, isLoading } = useCoupons(raffleId)
  const coupons = couponsResponse?.coupons || []

  const handleExport = async () => {
    setIsExporting(true)
    try {
      await GenerateRafflePdf({
        raffleId,
        userId: user?._id as string,
      })
      toast({
        title: 'PDF gerado com sucesso!',
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error(error)
      toast({
        variant: 'destructive',
        title:
          error?.message ||
          'Erro ao carregador os dados, tente novamente mais tarde!',
      })
    } finally {
      setIsExporting(false)
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={`rounded-xl p-8 sm:rounded-xl ${coupons?.length > 5 ? 'w-full' : 'w-[372px]'}`}
      >
        <DialogTitle className="text-xl">Meus Cupons</DialogTitle>
        <Separator />
        <div className="flex w-full items-center justify-between">
          <p className="text-[20px] font-semibold text-muted-foreground">
            {isLoading ? (
              <span className="text-black">Carregando cupons...</span>
            ) : (
              <span className="text-black">
                {couponsResponse?.pagination.total}
              </span>
            )}{' '}
            cupons
          </p>
          {couponsResponse?.exports && (
            <Button
              variant="outline"
              onClick={() => handleExport()}
              disabled={isLoading || isExporting}
            >
              <Stickynote className="mr-2" />
              {isLoading ? 'Gerando PDF...' : 'Exportar'}
            </Button>
          )}
        </div>

        <div
          id="coupon-list"
          className={`flex flex-col gap-[10px] ${
            coupons.length > 5 ? 'grid grid-cols-2' : ''
          }`}
        >
          {coupons.map((coupon) => (
            <div
              style={{ backgroundImage: couponImage ? `url(${couponImage})` : `url(/coupon.svg)`, padding: '10px' }}
              className="flex h-[48px] items-center rounded-md bg-cover bg-center bg-no-repeat"
              key={coupon?._id}
            >
              <span className="my-auto text-center font-semibold text-white">
                {coupon?.code}
              </span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default CouponModal
